import type { CreationView } from "@/shared/lib/creationFilters";
import { validateBrowseFilters, type AssetBrowseFilters } from "./assetBrowseFilters";
import type { AssetFilters } from "../api/foundation";
import type { V2Asset, V2Collection, V2Tag } from "../types";

export const ASSET_REFERENCE_SECTIONS = [
  "characters",
  "elements",
  "styles",
  "color",
  "locations",
] as const;

export type AssetReferenceSection =
  (typeof ASSET_REFERENCE_SECTIONS)[number];

const ASSET_REFERENCE_TAG_PREFIX = "afterform:reference:";
const ASSET_REFERENCE_SECTION_SET = new Set<AssetReferenceSection>(
  ASSET_REFERENCE_SECTIONS,
);

export type AssetStatusFilter = NonNullable<AssetFilters["status"]>;
export type AssetSort = NonNullable<AssetFilters["sort"]>;
export type AssetView = "grid" | "table";

export type AssetLibraryReadState =
  | "loading"
  | "ready"
  | "refreshing"
  | "partial"
  | "offline"
  | "permission_denied"
  | "failure";

export type AssetRequestIssue =
  | "offline"
  | "permission_denied"
  | "credit_blocked"
  | "conflict"
  | "failure";

export type AssetInspectionReadState =
  | "closed"
  | "ready"
  | "loading"
  | "offline"
  | "permission_denied"
  | "not_found"
  | "failure";

export interface AssetLibrarySearch extends AssetBrowseFilters {
  section?: "favorites" | "uploads" | "trash";
  layout?: CreationView;
  upload?: true;
  q?: string;
  media?: V2Asset["media_type"];
  status?: AssetStatusFilter;
  origin?: V2Asset["origin"];
  favorite?: true;
  tag?: string;
  collection?: string;
  project?: string;
  sort?: AssetSort;
  view?: AssetView;
  page?: number;
  asset?: string;
  reference?: AssetReferenceSection;
}

export interface ResolvedAssetLibrarySearch extends AssetBrowseFilters {
  section?: AssetLibrarySearch["section"];
  q: string;
  media: "all" | V2Asset["media_type"];
  status: "all" | AssetStatusFilter;
  origin: "all" | V2Asset["origin"];
  favorite: boolean;
  tag: string;
  collection: string;
  project: string;
  sort: AssetSort;
  view: AssetView;
  page: number;
  asset?: string;
  reference: AssetReferenceSection;
}

const MEDIA_TYPES = new Set<V2Asset["media_type"]>([
  "image",
  "video",
  "audio",
  "3d",
]);
const STATUSES = new Set<AssetStatusFilter>([
  "pending",
  "available",
  "archived",
  "deleting",
  "deletion_failed",
  "missing",
]);
const ORIGINS = new Set<V2Asset["origin"]>([
  "uploaded",
  "generated",
  "derived",
]);
const SORTS = new Set<AssetSort>([
  "newest",
  "oldest",
  "name",
  "recently_updated",
  "favorites_first",
]);

export const ASSET_PAGE_SIZE = 24;

export function validateAssetLibrarySearch(
  raw: Record<string, unknown>,
): AssetLibrarySearch {
  const page = Number(raw.page);
  return compactAssetLibrarySearch({
    ...validateBrowseFilters(raw),
    section: raw.section === "favorites" || raw.section === "uploads" || raw.section === "trash" ? raw.section : undefined,
    layout: raw.layout === "row" || raw.layout === "square" ? raw.layout : undefined,
    upload: raw.upload === true || raw.upload === "true" ? true : undefined,
    q: stringValue(raw.q),
    media: enumValue(raw.media, MEDIA_TYPES),
    status: enumValue(raw.status, STATUSES),
    origin: enumValue(raw.origin, ORIGINS),
    favorite:
      raw.favorite === true || raw.favorite === "true" ? true : undefined,
    tag: stringValue(raw.tag),
    collection: stringValue(raw.collection),
    project: stringValue(raw.project),
    sort: enumValue(raw.sort, SORTS),
    view: raw.view === "table" ? "table" : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
    asset: stringValue(raw.asset),
    reference: enumValue(raw.reference, ASSET_REFERENCE_SECTION_SET),
  });
}

export function assetBrowserSection(search: AssetLibrarySearch): "all" | "favorites" | "uploads" | "trash" {
  return search.section ?? (search.status === "archived" ? "trash" : "all");
}

export function normalizeAssetBrowserSearch(search: AssetLibrarySearch): AssetLibrarySearch {
  const section = assetBrowserSection(search);
  if (section === "favorites") return { ...search, favorite: true, unfavorited: undefined, status: undefined, origin: undefined, upload: undefined };
  if (section === "uploads") return { ...search, review: undefined, date: undefined, after: undefined, before: undefined, origin: "uploaded", favorite: undefined, status: undefined, media: undefined, unfavorited: undefined, downloaded: undefined, asset_tool: undefined, asset_style: undefined, asset_aspect: undefined, asset_model: undefined };
  if (section === "trash") return { ...search, status: "archived", favorite: undefined, unfavorited: undefined, origin: undefined, review: undefined, downloaded: undefined, upload: undefined };
  return search;
}

export function resolveAssetLibrarySearch(
  search: AssetLibrarySearch,
  fixedProjectID?: string,
): ResolvedAssetLibrarySearch {
  search = normalizeAssetBrowserSearch(search);
  return {
    ...validateBrowseFilters({ ...search }),
    ...(search.section ? { section: search.section } : {}),
    q: search.q ?? "",
    media: search.media ?? "all",
    status: search.status ?? "all",
    origin: search.origin ?? "all",
    favorite: search.favorite === true,
    tag: search.tag ?? "",
    collection: search.collection ?? "",
    project: fixedProjectID ?? search.project ?? "",
    sort: search.sort ?? "newest",
    view: search.view ?? "grid",
    page: search.page ?? 1,
    reference: search.reference ?? "characters",
    ...(search.asset ? { asset: search.asset } : {}),
  };
}

export function compactAssetLibrarySearch(
  search: AssetLibrarySearch,
): AssetLibrarySearch {
  search = normalizeAssetBrowserSearch(search);
  return {
    ...(search.section ? { section: search.section } : {}),
    ...(search.layout && search.layout !== "original" ? { layout: search.layout } : {}),
    ...validateBrowseFilters({ ...search }),
    ...(search.upload === true ? { upload: true as const } : {}),
    ...(search.q?.trim() ? { q: search.q } : {}),
    ...(search.media ? { media: search.media } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.origin ? { origin: search.origin } : {}),
    ...(search.favorite ? { favorite: true as const } : {}),
    ...(search.tag ? { tag: search.tag } : {}),
    ...(search.collection ? { collection: search.collection } : {}),
    ...(search.project ? { project: search.project } : {}),
    ...(search.sort && search.sort !== "newest" ? { sort: search.sort } : {}),
    ...(search.view === "table" ? { view: "table" as const } : {}),
    ...(search.page && search.page > 1 ? { page: search.page } : {}),
    ...(search.asset ? { asset: search.asset } : {}),
    ...(search.reference && search.reference !== "characters"
      ? { reference: search.reference }
      : {}),
  };
}

export function assetReferenceTagName(
  section: AssetReferenceSection,
): string {
  return `${ASSET_REFERENCE_TAG_PREFIX}${section}`;
}

export function assetReferenceSectionForTag(
  tag: Pick<V2Tag, "name">,
): AssetReferenceSection | undefined {
  const name = tag.name.toLocaleLowerCase();
  if (!name.startsWith(ASSET_REFERENCE_TAG_PREFIX)) return undefined;
  const section = name.slice(ASSET_REFERENCE_TAG_PREFIX.length);
  return ASSET_REFERENCE_SECTION_SET.has(section as AssetReferenceSection)
    ? (section as AssetReferenceSection)
    : undefined;
}

export function isAssetReferenceTag(tag: Pick<V2Tag, "name">): boolean {
  return assetReferenceSectionForTag(tag) !== undefined;
}

export function findAssetReferenceTag(
  tags: readonly V2Tag[],
  section: AssetReferenceSection,
): V2Tag | undefined {
  return tags.find(
    (tag) => assetReferenceSectionForTag(tag) === section,
  );
}

/**
 * Reference categories are persisted as canonical tags. Before categories
 * existed, uploaded Assets had no category at all; keep those legacy records
 * in the default Characters view so an upgrade never makes existing media
 * disappear. Assigning any reference tag moves them into the explicit views.
 */
export function filterAssetsForReferenceSection(
  assets: readonly V2Asset[],
  tags: readonly V2Tag[],
  section: AssetReferenceSection,
): V2Asset[] {
  const sectionTagIDs = new Map<AssetReferenceSection, string>();
  const categoryTagIDs = new Set<string>();
  for (const tag of tags) {
    const tagSection = assetReferenceSectionForTag(tag);
    if (!tagSection) continue;
    sectionTagIDs.set(tagSection, tag.id);
    categoryTagIDs.add(tag.id);
  }
  const selectedTagID = sectionTagIDs.get(section);
  return assets.filter((asset) => {
    if (selectedTagID && asset.tag_ids.includes(selectedTagID)) return true;
    const categorized = asset.tag_ids.some((tagID) =>
      categoryTagIDs.has(tagID),
    );
    return !categorized && section === "characters";
  });
}

export function assetFiltersFromSearch(
  search: ResolvedAssetLibrarySearch,
): AssetFilters {
  return {
    ...(search.media === "all" ? {} : { mediaType: search.media }),
    ...(search.status === "all" ? {} : { status: search.status }),
    ...(search.origin === "all" ? {} : { origin: search.origin }),
    ...(search.favorite ? { favorite: true } : {}),
    ...(search.tag ? { tagID: search.tag } : {}),
    ...(search.collection ? { collectionID: search.collection } : {}),
    ...(search.project ? { projectID: search.project } : {}),
    ...(search.q.trim() ? { search: search.q.trim() } : {}),
    sort: search.sort,
  };
}

export function resolveAssetLibraryReadState({
  hasLoadedData,
  isLoading,
  isPending = isLoading,
  isFetching,
  isError,
  online,
  errorStatus,
}: {
  hasLoadedData: boolean;
  isLoading: boolean;
  isPending?: boolean;
  isFetching: boolean;
  isError: boolean;
  online: boolean;
  errorStatus?: number;
}): AssetLibraryReadState {
  if (isError && hasLoadedData) return "partial";
  // TanStack reports a cold query as pending/paused, not loading or error,
  // when the browser is offline. It is not an authoritative empty result.
  if (!hasLoadedData && !online) return "offline";
  if ((isLoading || isPending) && !hasLoadedData) return "loading";
  if (isError && errorStatus === 403) return "permission_denied";
  if (isError) return "failure";
  if (isFetching) return "refreshing";
  return "ready";
}

export function resolveAssetInspectionReadState({
  requested,
  hasAsset,
  online,
  isPending,
  isError,
  isSuccess,
  errorStatus,
}: {
  requested: boolean;
  hasAsset: boolean;
  online: boolean;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  errorStatus?: number;
}): AssetInspectionReadState {
  if (!requested) return "closed";
  if (hasAsset) return "ready";
  if (!online) return "offline";
  if (isPending) return "loading";
  if (isError && errorStatus === 403) return "permission_denied";
  if (isError) return "failure";
  if (isSuccess) return "not_found";
  return "loading";
}

export function classifyAssetRequestIssue(
  errorStatus: number | undefined,
  online: boolean,
  errorCode?: string,
): AssetRequestIssue {
  if (!online) return "offline";
  if (errorStatus === 403) return "permission_denied";
  if (errorStatus === 402 && errorCode === "insufficient_credit") {
    return "credit_blocked";
  }
  if (errorStatus === 409 && errorCode === "version_conflict") {
    return "conflict";
  }
  return "failure";
}

export function rebaseCollectionWriteIntent<
  T extends { collection: V2Collection },
>(intent: T, authoritative: readonly V2Collection[]): T | null {
  const collection = authoritative.find(
    (candidate) => candidate.id === intent.collection.id,
  );
  return collection ? { ...intent, collection } : null;
}

export function assetCanBeSelected(asset: V2Asset): boolean {
  return asset.status !== "pending" && asset.status !== "deleting";
}

/** Keep canonical ingest/deletion readback moving only while visible work is active. */
export function assetLifecyclePollInterval(
  assets: readonly V2Asset[] | undefined,
): number | false {
  return assets?.some(
    (asset) => asset.status === "pending" || asset.status === "deleting",
  )
    ? 2_000
    : false;
}

export function selectedAssetActions(assets: V2Asset[]): {
  downloadable: V2Asset[];
  archivable: V2Asset[];
  restorable: V2Asset[];
  deletable: V2Asset[];
} {
  return {
    downloadable: assets.filter(
      (asset) =>
        asset.can_download &&
        (asset.status === "available" || asset.status === "archived"),
    ),
    archivable: assets.filter((asset) => asset.status === "available"),
    restorable: assets.filter((asset) => asset.status === "archived"),
    deletable: assets.filter(assetCanBeSelected),
  };
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function enumValue<T extends string>(
  value: unknown,
  values: ReadonlySet<T>,
): T | undefined {
  return typeof value === "string" && values.has(value as T)
    ? (value as T)
    : undefined;
}
